import { resetPassword } from "@/apis/auth/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateUserData,
  UpdateUserData,
  User,
  UserFilters,
  UserProfileUpdate,
  UserSearchParams,
  UserStatusAction,
} from "@/features/users/schemas/user";
import {
  assignUserPassword,
  createUserWithAuth,
  deleteUserService,
  getUserStatsService,
  getUsersByRoleService,
  getUsersService,
  resendVerificationEmail,
  searchUsersApi,
  searchUsersService,
  sendPasswordResetEmail,
  updateUserEmailApi,
  updateUserProfile,
  updateUserService,
  updateUserStatus,
  type UserServiceFilters,
} from "@/features/users/services/userService";
import {
  createUser,
  getActiveUsers,
  getUserById,
  getUsersPaginated,
  getUsersWithFilters,
  updateUserLastLogin,
} from "@/features/users/services/users";

// Query keys
export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (filters: UserFilters) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  stats: () => [...userKeys.all, "stats"] as const,
  byRole: (role: User["type"]) => [...userKeys.all, "role", role] as const,
};

// Hook para obtener todos los usuarios
export function useUsers(filters?: UserFilters) {
  return useQuery({
    queryKey: userKeys.list(filters || {}),
    queryFn: () => getUsersWithFilters(filters || {}),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

// Hook para obtener usuarios paginados
export function useUsersPaginated(
  page: number = 1,
  pageSize: number = 10,
  filters?: UserFilters
) {
  return useQuery({
    queryKey: [...userKeys.lists(), "paginated", { page, pageSize, filters }],
    queryFn: () => getUsersPaginated(page, filters),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook para obtener un usuario por ID
export function useUser(userId: string) {
  return useQuery({
    queryKey: userKeys.detail(userId),
    queryFn: () => getUserById(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook para obtener estadísticas de usuarios
export function useUserStats() {
  return useQuery({
    queryKey: userKeys.stats(),
    queryFn: () => getUserStatsService(),
    staleTime: 10 * 60 * 1000, // 10 minutos
  });
}

// Hook para obtener usuarios por rol
export function useUsersByRole(role: User["type"]) {
  return useQuery({
    queryKey: userKeys.byRole(role),
    queryFn: () => getUsersByRoleService(role),
    enabled: !!role,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook para obtener usuarios activos
export function useActiveUsers() {
  return useQuery({
    queryKey: [...userKeys.lists(), "active"],
    queryFn: () => getActiveUsers(),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook para crear un usuario
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userData,
      password,
    }: {
      userData: CreateUserData;
      password: string;
    }) => createUserWithAuth(userData, password, userData.name, userData.type),
    onSuccess: async () => {
      // Invalidar todas las consultas de usuarios
      await queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

// Hook para crear un usuario simple (solo Firestore)
export function useCreateUserSimple() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData: CreateUserData) => createUser(userData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

// Hook para actualizar un usuario
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, userData }: { userId: string; userData: UpdateUserData }) =>
      updateUserService(userId, userData),
    onSuccess: async (data, { userId }) => {
      if (data.success) {
        // Actualizar el cache del usuario específico
        queryClient.setQueryData(userKeys.detail(userId), data.data);
        // Invalidar todas las consultas de usuarios
        await queryClient.invalidateQueries({ queryKey: userKeys.all });
      }
    },
  });
}

// Hook para eliminar un usuario
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => deleteUserService(userId),
    onSuccess: async (data, userId) => {
      if (data.success) {
        // Remover el usuario del cache
        queryClient.removeQueries({ queryKey: userKeys.detail(userId) });
        // Invalidar todas las consultas de usuarios
        await queryClient.invalidateQueries({ queryKey: userKeys.all });
      }
    },
  });
}

// Hook para enviar email de restablecimiento de contraseña
export function useResetPassword() {
  return useMutation({
    mutationFn: (email: string) => resetPassword(email),
  });
}

// Hook para actualizar el último login
export function useUpdateUserLastLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => updateUserLastLogin(userId),
    onSuccess: async (_, userId) => {
      // Invalidar solo el usuario específico
      await queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
    },
  });
}

// Hook para buscar usuarios
export function useSearchUsers(searchTerm: string, filters?: UserFilters) {
  return useQuery({
    queryKey: [...userKeys.lists(), "search", { searchTerm, filters }],
    queryFn: () => searchUsersService(searchTerm, filters),
    enabled: !!searchTerm.trim(),
    staleTime: 2 * 60 * 1000, // 2 minutos
  });
}

// Hook para obtener usuarios con servicio (lógica de negocio)
export function useUsersService(filters?: UserServiceFilters) {
  return useQuery({
    queryKey: [...userKeys.lists(), "service", filters],
    queryFn: () => getUsersService(filters),
    staleTime: 5 * 60 * 1000,
  });
}

// Hook para prefetch de usuarios
export function usePrefetchUsers() {
  const queryClient = useQueryClient();

  return {
    prefetchUser: (userId: string) =>
      queryClient.prefetchQuery({
        queryKey: userKeys.detail(userId),
        queryFn: () => getUserById(userId),
        staleTime: 5 * 60 * 1000,
      }),

    prefetchUsers: (filters?: UserFilters) =>
      queryClient.prefetchQuery({
        queryKey: userKeys.list(filters || {}),
        queryFn: () => getUsersWithFilters(filters || {}),
        staleTime: 5 * 60 * 1000,
      }),
  };
}

/**
 * Busca usuarios usando el endpoint Typesense.
 * Con q="" devuelve todos los usuarios (carga inicial).
 */
export function useSearchUsersApi(params: UserSearchParams, enabled: boolean = true) {
  return useQuery({
    queryKey: [...userKeys.lists(), "typesense", params],
    queryFn: () => searchUsersApi(params),
    enabled,
    staleTime: 2 * 60 * 1000,
  });
}

/** Cambia el estado de un usuario (verify / enable / disable) */
export function useUpdateUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uid, action }: { uid: string; action: UserStatusAction }) =>
      updateUserStatus(uid, action),
    onSuccess: async (_, { uid }) => {
      await queryClient.invalidateQueries({ queryKey: userKeys.detail(uid) });
      await queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/**
 * Genera y asigna una contraseña aleatoria de 10 caracteres.
 * La contraseña devuelta debe comunicarse al usuario de forma segura.
 */
export function useAssignPassword() {
  return useMutation({
    mutationFn: (uid: string) => assignUserPassword(uid),
  });
}

/** Envía un correo de restablecimiento de contraseña usando el endpoint del API. */
export function useSendPasswordResetEmail() {
  return useMutation({
    mutationFn: (email: string) => sendPasswordResetEmail(email),
  });
}

/** Reenvía el correo de verificación de cuenta (endpoint público). */
export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) => resendVerificationEmail(email),
  });
}

/** Actualiza los campos editables del perfil de un usuario en Firestore. */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, data }: { uid: string; data: UserProfileUpdate }) =>
      updateUserProfile(uid, data),
    onSuccess: async (_, { uid }) => {
      await queryClient.invalidateQueries({ queryKey: userKeys.detail(uid) });
      await queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}

/** Actualiza el email de un usuario en Firebase Auth y Firestore. */
export function useUpdateUserEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, newEmail }: { uid: string; newEmail: string }) =>
      updateUserEmailApi(uid, newEmail),
    onSuccess: async (_, { uid }) => {
      await queryClient.invalidateQueries({ queryKey: userKeys.detail(uid) });
      await queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
}
/**
 * Hook para invalidar consultas de usuarios
 */
export function useInvalidateUserQueries() {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: userKeys.all }),
    invalidateLists: () => queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
    invalidateDetail: (id: string) =>
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) }),
    invalidateStats: () => queryClient.invalidateQueries({ queryKey: userKeys.stats() }),
  };
}
